import { Autocomplete, SxProps, Theme } from "@mui/material";
import { useEffect, useState } from "react";
import { useLogin } from "providers/Login";
import { findOptions, isChipKeyboardRemoval, optionValuesEqual, optionsFromDict, renderOptionChips, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically, withUnknownValues } from "utils/searchableOptions";

// Instance modes a rule or directory can be scoped to: the traffic nodes (icap,
// api) plus all-in-one, which covers both. Web and alert nodes handle no traffic,
// so offering them would let a user scope to something that can never apply it.
// The API filters server-side; this is the list we ask for.
const SHIELD_INSTANCE_MODES = ["icap", "api", "all"];

type InstanceList = { [instanceName: string]: string };

type Props = {
    value: string[];
    onChange: (value: string[]) => void;
    sx?: SxProps<Theme>;
};

export function ShieldInstancesSelect(props: Props) {
    const login = useLogin();
    const [instanceList, setInstanceList] = useState({} as InstanceList);

    useEffect(() => {
        const req = login.GetAxios();
        if (!req) {
            return;
        }

        // Joined into a single CSV value on purpose: axios would serialize an
        // array as mode[]=icap&mode[]=api, which the API reads as no filter at all.
        req.get("/api/instances", { params: { mode: SHIELD_INSTANCE_MODES.join(",") } })
            .then(resp => {
                const list: InstanceList = {};
                (resp.data.instances || []).forEach((x: any) => {
                    list[x.instanceName] = x.status === "disconnected"
                        ? `${x.instanceName} (disconnected)`
                        : x.instanceName;
                });
                setInstanceList(list);
            })
            .catch(() => { });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const options = withUnknownValues(sortOptionsAlphabetically(optionsFromDict(instanceList)), props.value);

    // Also shown when something is already selected but none are offered —
    // otherwise those selections stay in state and are still submitted on save,
    // with no way to see or clear them.
    if (Object.keys(instanceList).length === 0 && props.value.length === 0) {
        return null;
    }

    return <Autocomplete
        multiple
        disableCloseOnSelect
        disableClearable
        fullWidth
        sx={props.sx}
        id="icap-instances-select"
        options={options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={optionValuesEqual}
        value={findOptions(options, props.value)}
        onChange={(event, selected, reason) => {
            if (isChipKeyboardRemoval(event, reason)) {
                return;
            }
            props.onChange(selected.map(x => x.value));
        }}
        renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, props.value.some(v => v === option.value))}
        renderTags={(value, getTagProps) => renderOptionChips(value, getTagProps)}
        renderInput={(params) => searchTextField(params, { label: "Shield Instances" })}
    />;
}
